import React from 'react'
import { useFormikContext } from 'formik'
import { useNavigate } from 'react-router'
import { Alert } from '@toasttab/buffet-pui-alerts'
import { useFindReserveContext } from '../../contexts/find-reserve-context'
import CreditCardForm from '../CreditCardForm/CreditCardForm'
import { useCreditCardForm } from '../CreditCardForm/CreditCardFormProvider'
import { RestaurantInfo } from '../../api/restaurant/getRestaurant'
import { ReservationDetailEntryViewFormValues } from '../ReservationDetailEntryView/models'
import { PaymentStrategyType, PaymentType } from '../../api/payment/model'
import { useGetBooking } from '../../api/bookings/useGetBooking'
import { useFormatCurrency } from '../../hooks/useFormatCurrency'
import { NewLineFormatter } from '../NewLineFormatter/NewLineFormatter'
import { BookingCreationLegalText } from '../BookingCreationLegalText'
import { useAuth } from '@toasttab/do-secundo-guest-authentication'
import { useRestaurantContext } from '../../contexts/RestaurantContext'
import {
  OrderSummary,
  OrderSummaryType
} from '../ReservationConfirmationView/OrderSummary'
import { paymentHeaderTextMap } from '../ReservationDepositView/ReservationPaymentView'
import { getPaymentRefundText } from '../../utils/getPaymentRefundText'
import { track } from '@toasttab/do-secundo-analytics'
import { AnalyticsEvents } from '../../utils/analyticsEvents'
import { useBanquetProps } from 'banquet-runtime-modules'

const buttonText = 'Complete booking'

type CompleteBookingStepProps = {
  bookingGuid: string
  restaurant: RestaurantInfo
}

export const CompleteBookingStep = ({
  bookingGuid,
  restaurant
}: CompleteBookingStepProps) => {
  const spiEnabled = Boolean(
    useBanquetProps().featureFlags?.['nv1-spi-enabled']
  )
  const { isAuthenticated } = useAuth()
  const formatCurrency = useFormatCurrency()
  const { selectedAvailability } = useFindReserveContext()
  const { paymentError } = useCreditCardForm()
  const navigate = useNavigate()
  const formik = useFormikContext<ReservationDetailEntryViewFormValues>()
  const { data: booking } = useGetBooking(bookingGuid, restaurant.guid)
  const { shortUrl } = useRestaurantContext()

  return (
    <>
      <div className='flex flex-col gap-2'>
        <div className='flex flex-col gap-1'>
          {selectedAvailability?.payment?.policy && (
            <>
              <div className='text-secondary'>
                <NewLineFormatter text={selectedAvailability.payment.policy} />
              </div>
              <div className='border-b pt-4 mb-4' />
            </>
          )}
          {selectedAvailability?.payment?.paymentConfigType ===
          PaymentType.PREPAYMENT ? (
            <OrderSummary
              bookingGuid={bookingGuid}
              orderSummaryType={OrderSummaryType.AMOUNT_DUE}
              paymentRefundableCancellationDatetime={
                booking?.depositRefundableCancellationDatetime
              }
              hasPaymentBeenRefunded={false}
              bookableName={booking?.bookable?.name}
            />
          ) : (
            <>
              <h4 className='type-large font-semibold'>
                {booking?.paymentConfigSnapshot?.type
                  ? paymentHeaderTextMap[booking?.paymentConfigSnapshot?.type]
                  : null}
              </h4>
              <div className='flex justify-between'>
                <p>
                  Reservation
                  {selectedAvailability?.payment?.strategy.type ===
                  PaymentStrategyType.PARTYSIZE
                    ? ` x ${selectedAvailability.partySize}`
                    : ' x 1'}
                </p>
                <p>
                  {formatCurrency(
                    selectedAvailability?.payment?.strategy.paymentAmount || 0
                  )}
                </p>
              </div>
              <div className='border-b pt-2 mb-2' />
              <div className='flex flex-col gap-2'>
                <div className='flex justify-between type-headline-5 font-bold'>
                  <p>Amount Due</p>
                  <p>
                    {formatCurrency(
                      selectedAvailability?.payment?.strategy.paymentAmount ||
                        0,
                      { trailingZeros: true }
                    )}
                  </p>
                </div>
              </div>
            </>
          )}
          {selectedAvailability?.payment?.cancellationRefundableTimeframe !=
            null && (
            <div className='flex flex-col gap-2'>
              <p className='text-secondary'>
                {getPaymentRefundText(
                  selectedAvailability.payment.cancellationRefundableTimeframe,
                  selectedAvailability.payment.paymentConfigType
                )}
              </p>
            </div>
          )}
        </div>
        {paymentError && (
          <Alert variant='error' title='Payment Unsuccessful' className='my-2'>
            We were unable to run the payment method you provided. Please
            contact your bank or try using a different card.
          </Alert>
        )}
        {booking && (
          <CreditCardForm
            email={formik.values.email}
            restaurant={restaurant}
            onSuccess={(paymentDetails) => {
              track(AnalyticsEvents.RESERVATION_PAID, {
                isAuthenticated: isAuthenticated,
                paymentAmount: paymentDetails.amount,
                paymentProvider: paymentDetails.paymentProvider,
                spiEnabled: spiEnabled,
                selectedPaymentMethod:
                  paymentDetails.selectedPaymentMethod || 'unknown'
              })
              navigate(`/${shortUrl}/bookings/${booking.guid}`)
            }}
            checkGuid={booking.depositCheckId || ''}
            orderGuid={booking.depositOrderId || ''}
            paymentAmount={booking.depositAmount || 0}
          />
        )}
        <div className='text-secondary type-subhead'>
          <BookingCreationLegalText
            isAuthenticated={isAuthenticated}
            restaurant={restaurant}
            buttonText={buttonText}
          />
        </div>
      </div>
    </>
  )
}
